<template>
  <wa-drawer
    id="app-drawer"
    class="app-drawer"
    placement="start"
    light-dismiss
  >
    <div slot="label">
      <page-header-products :is-full="false" />
    </div>

    <div
      class="display-flex flex-column"
      style="gap: var(--spacing-3xl)"
    >
      <div>
        <site-link
          :to="routes.search.location"
          class="button flat clear with-border margin-bottom-lg display-flex flex-items-center rounded"
          style="
            --button-hover-color: var(--link-color);
            --border-color: var(--fa-navy);
            --button-hover-border-color: var(--fa-navy);
            box-shadow: var(--input-box-shadow);
            border-radius: var(--border-radius-pill);
            gap: var(--spacing-sm);
          "
          data-drawer="close"
          @click="$posthog.capture('main_navigation_link_click', { destination: 'icon_search', context: 'responsive_menu' })"
        >
          <font-awesome-icon
            :icon="$v7.solid.faMagnifyingGlass"
            style="color: var(--fa-navy)"
          />
          <span class="size-sm">Search {{ $counts.icons.all.formatted }} icons&hellip;</span>
        </site-link>

        <ul
          class="outline size-sm display-flex flex-column"
          style="gap: var(--spacing-xs)"
        >
          <li>
            <site-link
              v-if="$account"
              :to="routes.kits.location"
              class="display-flex flex-items-center padding-y-2xs no-underline"
              style="gap: var(--spacing-sm)"
              data-drawer="close"
              @click="$posthog.capture('main_navigation_link_click', { destination: 'kits', context: 'responsive_menu' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faBriefcase"
                size="lg"
              />
              Your Kits
            </site-link>
            <site-link
              v-else
              :to="routes.start.location"
              class="display-flex flex-items-center padding-y-2xs no-underline"
              style="gap: var(--spacing-sm)"
              data-drawer="close"
              @click="$posthog.capture('main_navigation_link_click', { destination: 'start', context: 'responsive_menu' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faRocketLaunch"
                size="lg"
              />
              Get Started
            </site-link>
          </li>
          <li>
            <site-link
              :to="routes.icons.location"
              class="display-flex flex-items-center padding-y-2xs no-underline"
              style="gap: var(--spacing-sm)"
              data-drawer="close"
              @click="$posthog.capture('main_navigation_link_click', { destination: 'icons', context: 'responsive_menu' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faIcons"
                size="lg"
              />
              Icons
            </site-link>
          </li>
          <li>
            <site-link
              :to="routes.ai.location"
              class="display-flex flex-items-center padding-y-2xs no-underline"
              style="gap: var(--spacing-sm)"
              data-drawer="close"
              @click="$posthog.capture('main_navigation_link_click', { destination: 'ai', context: 'responsive_menu' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faRectangleTerminal"
                size="lg"
              />
              AI
            </site-link>
          </li>
          <li>
            <site-link
              :to="routes.docs.location"
              class="display-flex flex-items-center padding-y-2xs no-underline"
              style="gap: var(--spacing-sm)"
              data-drawer="close"
              @click="$posthog.capture('main_navigation_link_click', { destination: 'docs', context: 'responsive_menu' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faBook"
                size="lg"
              />
              Documentation
            </site-link>
          </li>
          <li>
            <site-link
              :to="routes.plans.location"
              class="display-flex flex-items-center padding-y-2xs no-underline"
              style="gap: var(--spacing-sm)"
              data-drawer="close"
              @click="$posthog.capture('main_navigation_link_click', { destination: 'plans', context: 'responsive_menu' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faPlanetRinged"
                size="lg"
              />
              Plans
            </site-link>
          </li>
          <li>
            <site-link
              :to="routes.support.location"
              class="display-flex flex-items-center padding-y-2xs no-underline"
              style="gap: var(--spacing-sm)"
              data-drawer="close"
              @click="$posthog.capture('main_navigation_link_click', { destination: 'support', context: 'responsive_menu' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faHeadset"
                size="lg"
              />
              Support
            </site-link>
          </li>
        </ul>
      </div>

      <hr style="--hr-border-width: var(--border-width-xs); --hr-border-color: var(--fa-lt-gravy)" />

      <div
        class="display-flex flex-column"
        style="gap: var(--spacing-xl)"
      >
        <div
          class="display-flex flex-column muted"
          style="gap: var(--spacing-md)"
        >
          <font-awesome-icon
            :icon="$v7.brands.faFontAwesome"
            class="awesome-logo logo-font-awesome"
          />
          <h2 class="size-sm margin-y-0">Font Awesome</h2>
          <span class="size-xs muted">&copy; Fonticons, Inc.</span>
        </div>
      </div>

      <div
        class="display-flex flex-items-center size-sm"
        style="gap: var(--spacing-md)"
      >
        <h2 class="sr-only">Font Awesome Elsewhere</h2>
        <a
          href="https://github.com/fortawesome/Font-Awesome"
          rel="noopener noreferrer"
          target="_blank"
          class="link no-underline plain"
          aria-label="GitHub"
        >
          <font-awesome-icon :icon="$v7.brands.faGithub" />
        </a>
        <a
          href="https://www.podcastawesome.com"
          rel="noopener noreferrer"
          target="_blank"
          class="link no-underline plain"
          aria-label="Podcast Awesome"
        >
          <font-awesome-icon :icon="$v7.solid.faPodcast" />
        </a>
        <a
          href="https://www.youtube.com/@font.awesome"
          rel="noopener noreferrer"
          target="_blank"
          class="link no-underline plain"
          aria-label="YouTube"
        >
          <font-awesome-icon :icon="$v7.brands.faYoutube" />
        </a>
        <a
          href="https://bsky.app/profile/fontawesome.com"
          rel="noopener noreferrer"
          target="_blank"
          class="link no-underline plain"
          aria-label="Bluesky"
        >
          <font-awesome-icon :icon="$v7.brands.faBluesky" />
        </a>
        <a
          href="https://mastodon.social/@webawesome"
          rel="noopener noreferrer"
          target="_blank"
          class="link no-underline plain"
          aria-label="Mastodon"
        >
          <font-awesome-icon :icon="$v7.brands.faMastodon" />
        </a>
        <a
          href="https://x.com/fontawesome"
          rel="noopener noreferrer"
          target="_blank"
          class="link no-underline plain"
          aria-label="Twitter (X)"
        >
          <font-awesome-icon :icon="$v7.brands.faXTwitter" />
        </a>
        <a
          href="https://www.threads.com/@font.awesome"
          rel="noopener noreferrer"
          target="_blank"
          class="link no-underline plain"
          aria-label="Threads"
        >
          <font-awesome-icon :icon="$v7.brands.faThreads" />
        </a>
      </div>
    </div>
  </wa-drawer>
</template>

<script>
  import '@web.awesome.me/webawesome-pro/dist/components/drawer/drawer.js'
  import PageHeaderProducts from './PageHeaderProducts'
  import { PageHeaderRoutes } from './mixins'

  export default {
    name: 'PageHeaderDrawer',

    components: {
      PageHeaderProducts
    },

    mixins: [PageHeaderRoutes]
  }
</script>
